import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

interface DateVisibilitySettings {
  days_before: number;
  days_after: number;
}

const DEFAULT_SETTINGS: DateVisibilitySettings = {
  days_before: 30,
  days_after: 4,
};

async function fetchDateVisibilitySettings(): Promise<DateVisibilitySettings> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', 'date_visibility')
    .maybeSingle();

  if (error) {
    console.error('Error fetching date visibility settings:', error);
    return DEFAULT_SETTINGS;
  }

  if (data?.value) {
    const value = data.value as unknown as DateVisibilitySettings;
    return {
      days_before: value.days_before ?? DEFAULT_SETTINGS.days_before,
      days_after: value.days_after ?? DEFAULT_SETTINGS.days_after,
    };
  }

  return DEFAULT_SETTINGS;
}

export function useDateVisibility() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['date-visibility-settings'],
    queryFn: fetchDateVisibilitySettings,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    placeholderData: DEFAULT_SETTINGS, // Show default while loading
  });

  const updateSettings = async (newSettings: DateVisibilitySettings) => {
    const { error } = await supabase
      .from('app_settings')
      .update({ value: newSettings as unknown as Json })
      .eq('key', 'date_visibility');

    if (!error) {
      queryClient.setQueryData(['date-visibility-settings'], newSettings);
    }
    return { error };
  };

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ['date-visibility-settings'] });
  };

  return { 
    settings: settings ?? DEFAULT_SETTINGS, 
    isLoading, 
    updateSettings, 
    refetch 
  };
}
